import React, { Component } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBInput,
  MDBSpinner,
  MDBBadge,
} from "mdb-react-ui-kit";
import { listFilesForPicker, mfmThumbUrl, mfmPosterUrl, mfmUrl } from "../../Utils/MfmService";

/**
 * MfmFilePicker — Modal selector de ficheros existentes en MFM.
 *
 * Props:
 *   open        {boolean}  — Control de apertura
 *   onClose     {Function} — Callback al cerrar sin selección
 *   onSelect    {Function} — Callback({ uuid, url, thumbUrl, mediumUrl, mediaType, ... }) al seleccionar
 *   appCode     {string}   — Código de la app para filtrar ficheros accesibles
 *   mediaType   {string}   — "image" | "video" | "audio" | null (todos)
 *   title       {string}   — Título del modal (opcional)
 */
class MfmFilePicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      loading: false,
      error: null,
      search: "",
      page: 0,
      hasMore: true,
      selectedUuid: null,
    };
    this._searchTimer = null;
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      console.log('[MfmFilePicker] Abierto. appCode:', this.props.appCode, 'mediaType:', this.props.mediaType);
      this.setState(
        { files: [], page: 0, hasMore: true, selectedUuid: null, search: "", error: null },
        () => this.loadFiles()
      );
    }
  }

  loadFiles = async (reset = true) => {
    const { appCode, mediaType } = this.props;
    const { search, page } = this.state;
    const currentPage = reset ? 0 : page;

    this.setState({ loading: true, error: null });
    console.log('[MfmFilePicker] loadFiles — app:', appCode, 'mediaType:', mediaType, 'search:', search, 'page:', currentPage);
    try {
      const newFiles = await listFilesForPicker({
        app: appCode,
        mediaType: mediaType || undefined,
        search: search || undefined,
        page: currentPage,
        size: 24,
      });

      this.setState((prev) => ({
        files: reset ? newFiles : [...prev.files, ...newFiles],
        page: currentPage + 1,
        hasMore: newFiles.length === 24,
        loading: false,
      }));
      console.log('[MfmFilePicker] Cargados', newFiles.length, 'ficheros');
    } catch (err) {
      console.error('[MfmFilePicker] Error al cargar ficheros:', err);
      this.setState({ loading: false, error: "Error al cargar ficheros" });
    }
  };

  handleSearchChange = (e) => {
    const search = e.target.value;
    this.setState({ search, files: [], page: 0, hasMore: true }, () => {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.loadFiles(true), 400);
    });
  };

  handleSelect = (file) => {
    this.setState({ selectedUuid: file.uuid });
  };

  handleConfirm = () => {
    const { files, selectedUuid } = this.state;
    const file = files.find((f) => f.uuid === selectedUuid);
    if (file && this.props.onSelect) {
      this.props.onSelect(file);
    }
    this.handleClose();
  };

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  buildThumb = (file) => {
    if (file.mediaType === "image") {
      return file.thumbUrl || mfmThumbUrl(file.uuid) || mfmUrl(file.uuid);
    }
    if (file.mediaType === "video") {
      return file.posterUrl || mfmPosterUrl(file.uuid);
    }
    // audio: sin thumbnail
    return null;
  };

  renderVisibilityBadge(visibility) {
    const map = {
      private: { color: "secondary", label: "Privado" },
      groups: { color: "warning", label: "Grupos" },
      app: { color: "info", label: "App" },
      public: { color: "success", label: "Público" },
    };
    const v = map[visibility] || { color: "light", label: visibility };
    return (
      <MDBBadge color={v.color} pill className="ms-1" style={{ fontSize: "0.6rem" }}>
        {v.label}
      </MDBBadge>
    );
  }

  render() {
    const { open, title = "Seleccionar fichero existente" } = this.props;
    const { files, loading, error, search, selectedUuid, hasMore } = this.state;

    return (
      <MDBModal
        show={open}
        onHide={this.handleClose}
        tabIndex="-1"
      >
        <MDBModalDialog size="xl" scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{title}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>

            <MDBModalBody>
              {/* Buscador */}
              <div className="mb-3">
                <MDBInput
                  label="Buscar por nombre o título…"
                  value={search}
                  onChange={this.handleSearchChange}
                  icon="search"
                />
              </div>

              {/* Estado */}
              {error && (
                <div className="alert alert-danger">{error}</div>
              )}

              {/* Grid */}
              {files.length === 0 && !loading && (
                <p className="text-muted text-center py-4">No hay ficheros disponibles</p>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: "12px",
                }}
              >
                {files.map((file) => {
                  const thumb = this.buildThumb(file);
                  const isSelected = file.uuid === selectedUuid;

                  return (
                    <div
                      key={file.uuid}
                      onClick={() => this.handleSelect(file)}
                      style={{
                        cursor: "pointer",
                        border: isSelected ? "2px solid #1266f1" : "2px solid transparent",
                        borderRadius: "8px",
                        overflow: "hidden",
                        background: "#f5f5f5",
                        position: "relative",
                      }}
                    >
                      {/* Thumbnail */}
                      <div
                        style={{
                          width: "100%",
                          paddingBottom: "70%",
                          position: "relative",
                          background: "#e0e0e0",
                        }}
                      >
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={file.title || file.originalName}
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "2rem",
                            }}
                          >
                            {file.mediaType === "video" ? "🎬" : file.mediaType === "audio" ? "🎵" : "📄"}
                          </div>
                        )}

                        {/* Badge de visibilidad */}
                        <div style={{ position: "absolute", top: 4, right: 4 }}>
                          {this.renderVisibilityBadge(file.visibility)}
                        </div>

                        {/* Seleccionado */}
                        {isSelected && (
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              background: "rgba(18, 102, 241, 0.15)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "2rem",
                            }}
                          >
                            ✓
                          </div>
                        )}
                      </div>

                      {/* Nombre */}
                      <div
                        style={{
                          padding: "4px 6px",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={file.title || file.originalName}
                      >
                        {file.title || file.originalName}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load more */}
              {hasMore && !loading && files.length > 0 && (
                <div className="text-center mt-3">
                  <MDBBtn
                    size="sm"
                    color="secondary"
                    outline
                    onClick={() => this.loadFiles(false)}
                  >
                    Cargar más
                  </MDBBtn>
                </div>
              )}

              {loading && (
                <div className="text-center py-3">
                  <MDBSpinner role="status" />
                </div>
              )}
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={this.handleClose}>
                Cancelar
              </MDBBtn>
              <MDBBtn
                color="primary"
                disabled={!selectedUuid}
                onClick={this.handleConfirm}
              >
                Seleccionar
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default MfmFilePicker;
